import { useEffect, useMemo, useState } from "react";
import { api, currency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blankLine = { title: "", variant: "", quantity: 1, price: 0, cost_price: 0, vat_applicable: true };

export default function InvoiceEditDialog({ invoice, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) {
      setForm({
        customer_name: invoice.customer_name || "",
        customer_address: invoice.customer_address || "",
        customer_email: invoice.customer_email || "",
        customer_phone: invoice.customer_phone || "",
        customer_tag: invoice.customer_tag || "",
        customer_id: invoice.customer_id || null,
        order_number: invoice.order_number || "",
        invoice_date: (invoice.invoice_date || "").slice(0, 10),
        due_date: (invoice.due_date || "").slice(0, 10),
        notes: invoice.notes || "",
        line_items: (invoice.line_items || []).map((l) => ({ ...l })),
      });
    }
  }, [invoice]);

  const updateLine = (i, patch) =>
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
    }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { ...blankLine }] }));

  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, vat: 0, total: 0, cost: 0, profit: 0 };
    let subtotal = 0, vat = 0, cost = 0;
    for (const it of form.line_items) {
      const qty = parseInt(it.quantity) || 0;
      const line = (parseFloat(it.price) || 0) * qty;
      subtotal += line;
      cost += (parseFloat(it.cost_price) || 0) * qty;
      if (it.vat_applicable) vat += line * 0.2;
    }
    const r = (n) => Math.round(n * 100) / 100;
    return { subtotal: r(subtotal), vat: r(vat), total: r(subtotal + vat), cost: r(cost), profit: r(subtotal - cost) };
  }, [form]);

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (form.line_items.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      await api.put(`/invoices/${invoice.id}`, {
        ...form,
        line_items: form.line_items.map((it) => ({
          title: it.title,
          variant: it.variant || "",
          quantity: parseInt(it.quantity) || 1,
          price: parseFloat(it.price) || 0,
          cost_price: parseFloat(it.cost_price) || 0,
          vat_applicable: !!it.vat_applicable,
        })),
      });
      toast.success("Invoice updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch {
      toast.error("Failed to update invoice");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#032352]">
            Edit Invoice {invoice.invoice_number}
          </DialogTitle>
          <DialogDescription>Prices update the subtotal, VAT and total automatically.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Customer Name</Label>
            <Input
              data-testid="edit-customer-name"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Order Number</Label>
            <Input
              data-testid="edit-order-number"
              value={form.order_number}
              onChange={(e) => setForm({ ...form, order_number: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Invoice Date</Label>
              <Input
                type="date"
                data-testid="edit-invoice-date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                data-testid="edit-due-date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Address</Label>
            <Textarea
              value={form.customer_address}
              onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>Line Items</Label>
            <Button variant="outline" size="sm" onClick={addLine} data-testid="edit-add-line" className="h-8">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add line
            </Button>
          </div>
          <div className="space-y-2">
            {form.line_items.map((it, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`edit-line-${i}`}>
                <div className="flex gap-2">
                  <Input
                    placeholder="Description"
                    data-testid={`edit-line-title-${i}`}
                    value={it.title}
                    onChange={(e) => updateLine(i, { title: e.target.value })}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Variant"
                    value={it.variant}
                    onChange={(e) => updateLine(i, { variant: e.target.value })}
                    className="w-32"
                  />
                  <button
                    data-testid={`edit-remove-line-${i}`}
                    onClick={() => removeLine(i)}
                    className="text-slate-400 hover:text-red-500 transition-colors px-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#5B5C5F]">Qty</span>
                    <Input
                      type="number"
                      min="1"
                      data-testid={`edit-line-qty-${i}`}
                      value={it.quantity}
                      onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      className="w-16 h-9 text-center"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#5B5C5F]">Price £</span>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid={`edit-line-price-${i}`}
                      value={it.price}
                      onChange={(e) => updateLine(i, { price: e.target.value })}
                      className="w-24 h-9 text-right"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#5B5C5F]">Cost £</span>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid={`edit-line-cost-${i}`}
                      value={it.cost_price}
                      onChange={(e) => updateLine(i, { cost_price: e.target.value })}
                      className="w-24 h-9 text-right bg-slate-50"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[#5B5C5F]">VAT</span>
                    <Switch
                      data-testid={`edit-line-vat-${i}`}
                      checked={it.vat_applicable}
                      onCheckedChange={(v) => updateLine(i, { vat_applicable: v })}
                    />
                  </div>
                  <span className="ml-auto font-mono-num text-[#032352]">
                    {currency((parseFloat(it.price) || 0) * (parseInt(it.quantity) || 0))}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-[#5B5C5F]">Subtotal</span>
                <span className="font-mono-num" data-testid="edit-subtotal">{currency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5B5C5F]">VAT (20%)</span>
                <span className="font-mono-num" data-testid="edit-vat">{currency(totals.vat)}</span>
              </div>
              <div className="flex justify-between font-semibold text-[#032352] border-t pt-1">
                <span>Total</span>
                <span className="font-mono-num" data-testid="edit-total">{currency(totals.total)}</span>
              </div>
              <div className="flex justify-between text-xs text-[#5B5C5F]">
                <span>Profit (excl VAT − cost)</span>
                <span className="font-mono-num" data-testid="edit-profit">{currency(totals.profit)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <DialogFooter>
          <Button
            data-testid="save-invoice-edit"
            onClick={save}
            disabled={saving}
            className="bg-[#032352] hover:bg-[#04347A]"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
